'use strict';

import {
    trimString,
    toPositiveInt,
    toNonNegativeInt,
    appendQueryParams,
} from '../core/bambuserFormatters';
import { httpGetJson } from '../core/bambuserApiClient';
import {
    nativeCartMirror,
    resetNativeCartMirror,
} from '../core/bambuserSharedState';

var cartStateCache = null;
var cartStateCacheTimestamp = 0;
var CART_STATE_CACHE_TTL = 2000;
var pendingCartStateRequest = null;

function setCartStateCache (state, timestamp) {
    cartStateCache = state;
    cartStateCacheTimestamp = typeof timestamp === 'number' ? timestamp : Date.now();
}

export function invalidateCartStateCache () {
    cartStateCache = null;
    cartStateCacheTimestamp = 0;
    pendingCartStateRequest = null;
}

// ---------------------------------------------------------------------------
// PID normalisation for cart state lookup
// ---------------------------------------------------------------------------

function normalizePidKey (pid) {
    var normalized = trimString(pid);
    if (!normalized) {
        return null;
    }

    return normalized.replace(/\s+/g, '').toUpperCase();
}

function resolvePidFromCartState (cartState, pid) {
    var requestedPid = trimString(pid);
    var pidToLineItems =
        cartState &&
        cartState.pidToLineItems &&
        typeof cartState.pidToLineItems === 'object'
            ? cartState.pidToLineItems
            : null;

    if (!requestedPid || !pidToLineItems) {
        return requestedPid;
    }

    if (Array.isArray(pidToLineItems[requestedPid])) {
        return requestedPid;
    }

    var normalizedRequestedPid = normalizePidKey(requestedPid);
    if (!normalizedRequestedPid) {
        return requestedPid;
    }

    for (var key in pidToLineItems) {
        if (!Object.prototype.hasOwnProperty.call(pidToLineItems, key)) {
            continue;
        }

        if (normalizePidKey(key) === normalizedRequestedPid) {
            return key;
        }
    }

    return requestedPid;
}

// ---------------------------------------------------------------------------
// Line-item helpers
// ---------------------------------------------------------------------------

export function getPidLineItemsFromCartState (cartState, pid) {
    var resolvedPid = resolvePidFromCartState(cartState, pid);
    var lineItems =
        cartState &&
        cartState.pidToLineItems &&
        Array.isArray(cartState.pidToLineItems[resolvedPid])
            ? cartState.pidToLineItems[resolvedPid]
            : [];
    var normalized = [];

    for (var i = 0; i < lineItems.length; i++) {
        var lineItem = lineItems[i] || {};
        var uuid = trimString(lineItem.uuid || lineItem.UUID);
        if (!uuid) {
            continue;
        }

        normalized.push({
            uuid: uuid,
            quantity: toPositiveInt(
                lineItem.quantity || lineItem.quantityValue,
                1,
            ),
        });
    }

    return {
        pid: resolvedPid,
        lineItems: normalized,
    };
}

export function getLineItemsQuantityTotal (lineItems) {
    var total = 0;

    for (var i = 0; i < lineItems.length; i++) {
        total += toPositiveInt(lineItems[i] && lineItems[i].quantity, 1);
    }

    return total;
}

export function getPidQuantityFromCartModel (cartModel, pid) {
    if (!cartModel || !Array.isArray(cartModel.items)) {
        return null;
    }

    var quantity = 0;

    for (var i = 0; i < cartModel.items.length; i++) {
        var item = cartModel.items[i] || {};
        var itemPid = trimString(item.id || item.productID || item.pid);
        if (itemPid !== pid) {
            continue;
        }

        quantity += toPositiveInt(
            item.quantity || item.quantityValue || item.qty,
            1,
        );
    }

    return quantity;
}

function buildRemovalQueue (lineItems, maxRemovals) {
    var queue = [];

    for (var i = 0; i < lineItems.length && queue.length < maxRemovals; i++) {
        var lineItem = lineItems[i];
        var uuid = lineItem && lineItem.uuid;
        var quantity = toPositiveInt(lineItem && lineItem.quantity, 1);
        if (!uuid) {
            continue;
        }

        for (var j = 0; j < quantity && queue.length < maxRemovals; j++) {
            queue.push(uuid);
        }
    }

    return queue;
}

// ---------------------------------------------------------------------------
// Native cart state fetch & mirror sync
// ---------------------------------------------------------------------------

export function fetchNativeCartState (config) {
    if (!config || !config.cartStateUrl) {
        return Promise.reject(new Error('Missing cartStateUrl'));
    }

    if (pendingCartStateRequest) {
        return pendingCartStateRequest;
    }

    pendingCartStateRequest = httpGetJson(config.cartStateUrl).then(
        function (state) {
            setCartStateCache(state);
            pendingCartStateRequest = null;
            return state;
        },
        function (error) {
            pendingCartStateRequest = null;
            throw error;
        },
    );

    return pendingCartStateRequest;
}

export function syncNativeCartMirrorFromState (cartState) {
    var pidToLineItems =
        cartState &&
        cartState.pidToLineItems &&
        typeof cartState.pidToLineItems === 'object'
            ? cartState.pidToLineItems
            : {};
    var updatedMirror = {};

    for (var pid in pidToLineItems) {
        if (!Object.prototype.hasOwnProperty.call(pidToLineItems, pid)) {
            continue;
        }

        var pidState = getPidLineItemsFromCartState(cartState, pid);
        var quantity = getLineItemsQuantityTotal(pidState.lineItems);
        if (quantity > 0) {
            updatedMirror[pidState.pid || pid] = quantity;
        }
    }

    resetNativeCartMirror(updatedMirror);
    return nativeCartMirror;
}

function getCachedCartState (config) {
    var now = Date.now();

    if (pendingCartStateRequest) {
        return pendingCartStateRequest;
    }

    if (cartStateCache && (now - cartStateCacheTimestamp) < CART_STATE_CACHE_TTL) {
        return Promise.resolve(cartStateCache);
    }

    return fetchNativeCartState(config).then(function (state) {
        setCartStateCache(state, now);
        return state;
    });
}

// ---------------------------------------------------------------------------
// Cart decrease (item removal)
// ---------------------------------------------------------------------------

export function syncNativeCartDecrease (
    pid,
    targetQty,
    config,
    cartStateSnapshot,
) {
    var cartStatePromise = cartStateSnapshot
        ? Promise.resolve(cartStateSnapshot)
        : getCachedCartState(config);

    return cartStatePromise.then(function (cartState) {
        var removeUrl =
            trimString(cartState && cartState.removeProductLineItemUrl) ||
            trimString(config && config.removeFromCartUrl);
        var pidState = getPidLineItemsFromCartState(cartState, pid);
        var effectivePid = pidState.pid || pid;
        var lineItems = pidState.lineItems;
        var currentQty = getLineItemsQuantityTotal(lineItems);
        var normalizedTargetQty = toNonNegativeInt(targetQty, 0);
        var removeCount = Math.max(0, currentQty - normalizedTargetQty);

        if (removeCount <= 0) {
            return { pid: effectivePid, quantity: normalizedTargetQty };
        }

        if (!removeUrl) {
            throw new Error('Missing remove url');
        }

        var queue = buildRemovalQueue(lineItems, removeCount);
        if (!queue.length) {
            throw new Error('No removable line items for pid');
        }

        function removeAtIndex (index, lastResponse) {
            if (index >= removeCount) {
                var syncedQty = getPidQuantityFromCartModel(
                    lastResponse && lastResponse.cartModel,
                    effectivePid,
                );

                if (syncedQty === null) {
                    syncedQty = Math.max(0, currentQty - removeCount);
                }

                return Promise.resolve({
                    pid: effectivePid,
                    quantity: syncedQty,
                });
            }

            var uuid = queue[index] || queue[queue.length - 1];
            var requestUrl = appendQueryParams(removeUrl, {
                pid: effectivePid,
                uuid: uuid,
            });

            return httpGetJson(requestUrl).then(function (response) {
                return removeAtIndex(index + 1, response);
            });
        }

        return removeAtIndex(0, null).then(function (result) {
            invalidateCartStateCache();
            return result;
        });
    });
}
